import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Plus, Pencil, Copy, Search, X, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Api } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { SurgeDialog, SurgeRow } from '@/components/features/surge/surge-dialog'
import { DOW_OPTIONS } from '@/components/features/surge/surge-dow'

export const Route = createFileRoute('/_auth/surge')({
    component: RouteComponent,
})

function RouteComponent() {
    const { token, setPage, role, email } = useStore(useShallow(state => ({
        token: state.token,
        setPage: state.setPage,
        role: state.selectedAccount.role,
        email: state.email,
    })))

    // An internal prorouting.in user, or an admin/super_admin on this account (/admin/ matches
    // both role strings). Everyone can still view the page and its list; this gates every action
    // that adds/changes a surge (add, edit, duplicate, activate/deactivate).
    const canManageSurges = (email?.endsWith('prorouting.in')) || (/admin/.test(role || ''))

    useEffect(() => { setPage('Surge') }, [])

    const queryClient = useQueryClient()

    const surgesQuery = useQuery({
        queryKey: ['client_surges', token],
        queryFn: () => Api<any>('/webui/client_surge/list', 'POST', {}, { token }),
        staleTime: 30 * 1000,
    })

    const toggleStatus = useMutation({
        mutationFn: ({ id, status }: { id: number; status: string }) =>
            Api('/webui/client_surge/update', 'POST', { id, status }, { token }),
        onSuccess(data: any) {
            if (data?.status !== 1) { toast.error('Failed to update status'); return }
            queryClient.invalidateQueries({ queryKey: ['client_surges'] })
        },
        onError() { toast.error('Failed to update status') },
    })

    // Stored as 'mon,sat,sun'; shown as 'Mon, Sat, Sun' in week order.
    const formatDow = (dow?: string | null) => {
        const days = (dow || '').split(',').map(d => d.trim())
        return DOW_OPTIONS.filter(d => days.includes(d.value)).map(d => d.label).join(', ')
    }
    const formatStoreIds = (storeIds?: string | null) =>
        (storeIds || '').split(',').map(s => s.trim()).filter(Boolean).join(', ')

    const refresh = () => queryClient.invalidateQueries({ queryKey: ['client_surges'] })

    const [tab, setTab] = useState<'present' | 'past'>('present')
    const [search, setSearch] = useState('')

    type SortKey = 'id' | 'city' | 'amount' | 'start_date' | 'end_date' | 'start_time' | 'end_time' | 'status' | 'notes'
    const [sortKey, setSortKey] = useState<SortKey | null>(null)
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

    const sortValue = (row: SurgeRow, key: SortKey): string | number => {
        switch (key) {
            case 'id': return row.id ?? 0
            case 'city': return (row.city || '').toLowerCase()
            case 'amount': return row.surge_config?.default ?? -Infinity
            case 'start_date': return row.start_date?.slice(0, 10) || ''
            case 'end_date': return row.end_date?.slice(0, 10) || ''
            case 'start_time': return row.start_time?.slice(0, 5) || ''
            case 'end_time': return row.end_time?.slice(0, 5) || ''
            case 'status': return row.status || ''
            case 'notes': return (row.notes || '').toLowerCase()
        }
    }

    const toggleSort = (key: SortKey) => {
        if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return }
        setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    }

    const SortIcon = ({ column }: { column: SortKey }) => {
        if (sortKey !== column) return <ArrowUpDown className="w-3 h-3 text-gray-300" />
        return sortDir === 'asc'
            ? <ArrowUp className="w-3 h-3 text-gray-700" />
            : <ArrowDown className="w-3 h-3 text-gray-700" />
    }

    // Inactive surges (deactivated via the status toggle) are dropped entirely, not just
    // greyed out - they're clutter once turned off, not something to keep browsing.
    const allRows: SurgeRow[] = (surgesQuery.data?.surges ?? []).filter((r: SurgeRow) => r.status !== 'inactive')

    // Free-text search across city, notes, status and amount.
    const q = search.trim().toLowerCase()
    const searched = q
        ? allRows.filter(r =>
            [
                r.city,
                r.storeIds,
                r.notes,
                r.status,
                r.surge_config?.default,
            ]
                .filter(v => v != null)
                .some(v => String(v).toLowerCase().includes(q)),
        )
        : allRows

    // Today (local) as YYYY-MM-DD. A surge is "past" once its end date is before today; one with no
    // end date (e.g. a recurring evening/Sunday surge) never ends, so it's always present.
    const today = new Date().toLocaleDateString('en-CA')
    const isPast = (r: SurgeRow) => !!r.end_date && r.end_date.slice(0, 10) < today
    const presentRows = searched.filter(r => !isPast(r))
    const pastRows = searched.filter(isPast)
    const tabRows = tab === 'present' ? presentRows : pastRows
    const rows = sortKey
        ? [...tabRows].sort((a, b) => {
            const va = sortValue(a, sortKey)
            const vb = sortValue(b, sortKey)
            const cmp = va < vb ? -1 : va > vb ? 1 : 0
            return sortDir === 'asc' ? cmp : -cmp
        })
        : tabRows

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-semibold">Client Surge</h1>
                {canManageSurges ? (
                    <SurgeDialog onSuccess={refresh}>
                        <Button size="sm">
                            <Plus className="w-4 h-4 mr-1" /> Add Surge
                        </Button>
                    </SurgeDialog>
                ) : null}
            </div>

            <div className="relative max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search city, store, notes (e.g. DP, Faridabad)…"
                    className="w-full pl-8 pr-8 py-1.5 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300"
                />
                {search && (
                    <button
                        onClick={() => setSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            <div className="flex gap-1 border-b border-gray-200">
                {([
                    ['present', 'Present', presentRows.length],
                    ['past', 'Past', pastRows.length],
                ] as const).map(([key, label, count]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
                            tab === key
                                ? 'border-black text-black'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        {label} <span className="text-gray-400">({count})</span>
                    </button>
                ))}
            </div>

            {surgesQuery.isLoading && <p className="text-sm text-gray-500">Loading…</p>}
            {surgesQuery.isError && <p className="text-sm text-red-500">Failed to load surges.</p>}

            {!surgesQuery.isLoading && rows.length === 0 && (
                <p className="text-sm text-gray-400">No {tab} surge entries found.</p>
            )}

            {rows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('id')} className="flex items-center gap-1 hover:text-gray-900">
                                        ID <SortIcon column="id" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('city')} className="flex items-center gap-1 hover:text-gray-900">
                                        City <SortIcon column="city" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-right">
                                    <button onClick={() => toggleSort('amount')} className="flex items-center gap-1 ml-auto hover:text-gray-900">
                                        Amount (₹) <SortIcon column="amount" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('start_date')} className="flex items-center gap-1 hover:text-gray-900">
                                        Start Date <SortIcon column="start_date" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('end_date')} className="flex items-center gap-1 hover:text-gray-900">
                                        End Date <SortIcon column="end_date" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('start_time')} className="flex items-center gap-1 hover:text-gray-900">
                                        Start Time <SortIcon column="start_time" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('end_time')} className="flex items-center gap-1 hover:text-gray-900">
                                        End Time <SortIcon column="end_time" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">Days</th>
                                <th className="px-3 py-2 text-left">Stores</th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('status')} className="flex items-center gap-1 hover:text-gray-900">
                                        Status <SortIcon column="status" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">
                                    <button onClick={() => toggleSort('notes')} className="flex items-center gap-1 hover:text-gray-900">
                                        Notes <SortIcon column="notes" />
                                    </button>
                                </th>
                                <th className="px-3 py-2 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map(row => (
                                <tr key={row.id} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-gray-400">{row.id}</td>
                                    <td className="px-3 py-2">{row.city || <span className="text-gray-400">all</span>}</td>
                                    <td className="px-3 py-2 text-right font-medium">
                                        {row.surge_config?.default ?? '—'}
                                    </td>
                                    <td className="px-3 py-2">{row.start_date?.slice(0, 10) || '—'}</td>
                                    <td className="px-3 py-2">{row.end_date?.slice(0, 10) || '—'}</td>
                                    <td className="px-3 py-2">{row.start_time?.slice(0, 5) || '—'}</td>
                                    <td className="px-3 py-2">{row.end_time?.slice(0, 5) || '—'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        {formatDow(row.dow) || <span className="text-gray-400">all</span>}
                                    </td>
                                    <td className="px-3 py-2 max-w-[160px] truncate" title={formatStoreIds(row.storeIds)}>
                                        {formatStoreIds(row.storeIds) || <span className="text-gray-400">all</span>}
                                    </td>
                                    <td className="px-3 py-2">
                                        {canManageSurges ? (
                                            <button
                                                onClick={() => toggleStatus.mutate({
                                                    id: row.id!,
                                                    status: row.status === 'active' ? 'inactive' : 'active',
                                                })}
                                                className={`px-2 py-0.5 rounded-full text-xs font-semibold cursor-pointer ${
                                                    row.status === 'active'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-100 text-gray-500'
                                                }`}
                                            >
                                                {row.status}
                                            </button>
                                        ) : (
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                row.status === 'active'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-gray-100 text-gray-500'
                                            }`}>
                                                {row.status}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 max-w-[160px] truncate" title={row.notes || ''}>
                                        {row.notes || '—'}
                                    </td>
                                    <td className="px-3 py-2">
                                        {canManageSurges ? (
                                            <div className="flex items-center gap-3">
                                                <SurgeDialog existing={row} onSuccess={refresh}>
                                                    <button className="text-gray-400 hover:text-blue-600" title="Edit">
                                                        <Pencil className="w-4 h-4" />
                                                    </button>
                                                </SurgeDialog>
                                                <SurgeDialog duplicateOf={row} onSuccess={refresh}>
                                                    <button className="text-gray-400 hover:text-blue-600" title="Duplicate (city cleared)">
                                                        <Copy className="w-4 h-4" />
                                                    </button>
                                                </SurgeDialog>
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
